import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { useEffect, useState, useContext } from "react";
import { convertToCSV } from "@solsys/solsys-common";
import {
  OptionsMenu,
  DateTimeInput,
  Button,
  NotificationContext,
  ResponsiveTableView,
  Tabs,
} from "@solsys/solsys-reactjs";
import { DateTime } from "luxon";
import { FaPlus, FaCog, FaFileCsv } from "react-icons/fa";
import { isTechnician } from "../../utils/roles";
import { useAuth } from "../../contexts/AuthContext";
import TagDropDown from "../../dropdowns/TagDropDown";
import RegionDropDown from "../../dropdowns/RegionDropDown";
import CustomerDropDown from "../../dropdowns/CustomerDropDown";
import { withTimestampSortOptions, resetListSort } from "../../listSort";
import ListView from "../mapview/ListView";

const gqlQuery = gql`
  query craneList($crane_list_request: crane_list_request,
  $tag2Crane_list_request: tag2Crane_list_request)
  {
    crane(crane_list_request: $crane_list_request) {
      edges {
        id
        name
        region {
          name
        }
        region_id
        last_activity_at
      }
      totalCount
    }
    tag2Crane(tag2Crane_list_request: $tag2Crane_list_request) {
      edges {
        id
        crane_id
      }
      totalCount
    }
  }
`;

const ticketCountQuery = gql`
  query ticketCounts($ticket_list_request: ticket_list_request) {
    ticket(ticket_list_request: $ticket_list_request) {
      edges {
        id
        crane_id
      }
      totalCount
    }
  }
`;

const craneSubscriptionQuery = gql`
  query craneSubscriptions($crane_subscription_list_request: crane_subscription_list_request) {
    crane_subscription(crane_subscription_list_request: $crane_subscription_list_request) {
      edges {
        id
        crane_id
        user_id
      }
    }
  }
`;

export const ListOptions = {
  sortBy: "sortBy",
  sortDirection: "sortDirection",
  name: "name",
  tab: "tab",
  tagIds: "tag_ids",
  regionId: "region_id",
  customerId: "customer_id",
  startDate: 'start_date',
  endDate: 'end_date',
  subscribedOnly: "subscribed_only",
  search: "search",
};

export default function CraneList({ id: entityId, action, ...props }) {
  const { addNotification } = useContext(NotificationContext);
  const { user } = useAuth();
  const isAdd = action === "add";

  const [requestState, setRequestState] = props.state;
  let { sortBy, sortDirection, name, tab, startDate, endDate, tagIds, regionId, customerId, subscribedOnly, search } = requestState;

  const isTech = isTechnician(user);
  if (subscribedOnly === undefined && isTech) {
    subscribedOnly = true;
    setRequestState({ ...requestState, subscribedOnly: true });
  }
  const setListOptions = (state) =>
    setRequestState({ ...requestState, ...state });
  const [optionSortBy, setOptionSortBy] = useState(sortBy);
  const [optionSortDirection, setOptionSortDirection] = useState(sortDirection);
  const [optionName, setOptionName] = useState(name);
  const [optionTab, setOptionTab] = useState(tab);
  const [activeTab, setActiveTab] = useState('Data');
  const [optionTagIds, setOptionTagIds] = useState(tagIds ? tagIds : []);
  const [optionRegionId, setOptionRegionId] = useState(regionId);
  const [optionCustomerId, setOptionCustomerId] = useState(customerId);
  const [optionStartDate, setOptionStartDate] = useState(null);
  const [optionEndDate, setOptionEndDate] = useState(null);
  const [optionSubscribedOnly, setOptionSubscribedOnly] = useState(subscribedOnly !== undefined ? subscribedOnly : (isTech ? true : false));
  const applyOptions = () => {
    setListOptions({
      sortBy: optionSortBy,
      sortDirection: optionSortDirection,
      name: optionName,
      tab: optionTab,
      tagIds: optionTagIds.length > 0 ? optionTagIds : null,
      regionId: optionRegionId,
      customerId: optionCustomerId,
      startDate: optionStartDate,
      endDate: optionEndDate,
      subscribedOnly: optionSubscribedOnly,
    });
  };
  const resetOptions = () => {
    const defaults = resetListSort();
    setOptionSortDirection(defaults.sortDirection);
    setOptionSortBy(defaults.sortBy);
    setOptionName("");
    setOptionTab("Chat");
    setOptionTagIds([]);
    setOptionRegionId(null);
    setOptionCustomerId(null);
    setOptionStartDate(null);
    setOptionEndDate(null);
    setOptionSubscribedOnly(isTech ? true : false);
  };

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [limit, setLimit] = useState(100);

  const sortByOptions = withTimestampSortOptions([
    { text: "Crane ID", sortBy: "id" },
    { text: "Name", sortBy: "name" },
    { text: "Last Activity", sortBy: "last_activity_at" },
  ]);
  const sortDirectionOptions = [
    {
      text: "A to Z",
      sortDirection: "ASC",
    },
    {
      text: "Z to A",
      sortDirection: "DESC",
    },
  ];

  let variables = {
    crane_list_request: {
      filters: [],
      pagination: { limit },
      sorting: [],
    },
    tag2Crane_list_request: {
      filters: [{
        field: "tag_id",
        operator: "in",
        value: `(${tagIds ? tagIds.join(", ") : ''})`,
      }]
    }
  };
  if (sortBy && sortDirection) {
    let sortOption = sortByOptions.find((v) => v.sortBy === sortBy);
    variables.crane_list_request.sorting.push({
      field: sortBy,
      direction: sortDirection,
      entity: sortOption?.sortEntity,
    });
  }
  if (name) {
    variables.crane_list_request.filters.push({
      field: "name",
      operator: "like",
      value: `%${name}%`,
    });
  }
  if (search?.trim()) {
    variables.crane_list_request.filters.push({
      field: "search",
      value: search.trim(),
    });
  }
  if (regionId) {
    variables.crane_list_request.filters.push({
      field: "region_id",
      operator: "=",
      value: regionId.toString(),
    });
  }
  if (customerId) {
    variables.crane_list_request.filters.push({
      field: "customer_id",
      operator: "=",
      value: customerId.toString(),
    });
  }
  if (startDate || endDate) {
    if (startDate && endDate) {
      variables.crane_list_request.filters.push({
        field: "created_at",
        operator: "between",
        value: `${startDate.toString()},${endDate.toString()}`,
      });
    } else if (startDate) {
      variables.crane_list_request.filters.push({
        field: "created_at",
        operator: ">",
        value: startDate.toString(),
      });
    } else if (endDate) {
      variables.crane_list_request.filters.push({
        field: "created_at",
        operator: "<",
        value: endDate.toString(),
      });
    }
  }

  const [csvQuery, { loading: csvLoading }] = useLazyQuery(gqlQuery);
  const [list, setList] = useState([]);
  const [queryData, setQueryData] = useState(null);
  const [ticketCounts, setTicketCounts] = useState({});
  const [subscribedCraneIds, setSubscribedCraneIds] = useState([]);

  const { loading: subscriptionLoading } = useQuery(craneSubscriptionQuery, {
    variables: {
      crane_subscription_list_request: {
        filters: [
          {
            field: "user_id",
            operator: "=",
            value: String(user?._id || ""),
          },
        ],
      },
    },
    skip: !user?._id || !subscribedOnly,
    fetchPolicy: "network-only",
    onCompleted: (subscriptionData) => {
      const subscriptions = subscriptionData?.crane_subscription?.edges ?? [];
      const craneIds = subscriptions.map((sub) => sub.crane_id);
      setSubscribedCraneIds(craneIds);
    },
    onError: (error) => {
      console.error("Error fetching crane subscriptions:", error);
    },
  });

  const { loading: ticketCountLoading, refetch: refetchTicketCounts } = useQuery(ticketCountQuery, {
    variables: {
      ticket_list_request: { filters: [{ field: "status_id", operator: "=", value: "1" }], pagination: { limit: 10000 } },
    },
    fetchPolicy: "network-only",
    onCompleted: (ticketData) => {
      const tickets = ticketData?.ticket?.edges ?? [];
      const counts = {};
      tickets.forEach((t) => {
        counts[t.crane_id] = (counts[t.crane_id] || 0) + 1;
      });
      setTicketCounts(counts);
    },
    onError: (error) => console.error("Error fetching ticket counts:", error),
  });

  const [refetch, { loading, data }] = useLazyQuery(gqlQuery, {
    variables,
    pollInterval: 5000,
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
  });

  useEffect(() => {
    if (data) setQueryData(data);
  }, [data]);

  const selectItem = (id) => {
    props.onSelect(id, true);
  }

  const setStartDate = (date) => {
    if (!date) setOptionStartDate(null);
    else setOptionStartDate(date.toString() === 'Invalid DateTime' ? null : date.toString());
  }

  const setEndDate = (date) => {
    if (!date) setOptionEndDate(null);
    else {
      if (date.toString() === 'Invalid DateTime') {
        setOptionEndDate(null);
      } else {
        const newDate = date.plus({ days: 1, seconds: -1 });
        setOptionEndDate(newDate.toString());
      }
    }
  }

  const count = data?.crane?.totalCount ?? 0;

  const formatLastActivity = (iso) => {
    if (!iso) return "—";
    const dt = DateTime.fromISO(iso, { zone: "utc" });
    if (!dt.isValid) return "—";
    return dt.toLocal().toFormat("MMM d, yyyy h:mm a");
  };

  if (props.isMaster && props.autoSelect && !loading && !isAdd && !entityId) {
    props.onSelect(list.length ? list[0].id : 0);
  }

  useEffect(() => {
    if (props.onStateChange) props.onStateChange(requestState);
    refetch({ variables });
    refetchTicketCounts();
  }, [requestState, limit]);

  useEffect(() => {
    if (!queryData) return;

    const cranes = queryData?.crane?.edges ?? [];
    const tag2Cranes = queryData?.tag2Crane?.edges ?? [];
    const crane_ids = tag2Cranes.map(item => item.crane_id);
    let filteredCranes = cranes.filter((crane) => crane_ids.includes(crane.id) || !tagIds);

    if (subscribedOnly) {
      if (subscriptionLoading) {
        return;
      }
      filteredCranes = filteredCranes.filter((crane) => subscribedCraneIds.includes(crane.id));
    }

    setList(filteredCranes);
  }, [queryData, subscribedOnly, subscribedCraneIds, tagIds, subscriptionLoading]);

  return (
    <OptionsMenu isOpen={ isFilterMenuOpen }>
      <OptionsMenu.Menu>
        <div className="options-tools">
          <Button
            onClick={ () => {
              applyOptions();
              setIsFilterMenuOpen(false);
            } }
            loading={ loading || ticketCountLoading || subscriptionLoading }
          >
            Apply
          </Button>
          <button onClick={ resetOptions }>Reset</button>
          <button onClick={ () => setIsFilterMenuOpen(false) }>Close</button>
        </div>

        <div className="options-section">
          <h3>Filters</h3>
          <div className="option">
            <label>Crane Name:</label>
            <input
              value={ optionName }
              onChange={ (e) => setOptionName(e.target.value) }
            />
          </div>
          <div className="option">
            <label>Tags:</label>
            <TagDropDown
              selected={ optionTagIds }
              onSelect={ setOptionTagIds }
              searchEnabled
              multiple={ true }
            />
          </div>
          <div className="option">
            <label>Region:</label>
            <RegionDropDown
              selected={ optionRegionId }
              onSelect={ setOptionRegionId }
              searchEnabled
            />
          </div>
          <div className="option">
            <label>Customer:</label>
            <CustomerDropDown
              selected={ optionCustomerId }
              onSelect={ setOptionCustomerId }
              searchEnabled
            />
          </div>
          <div className="option">
            <label>
              <input
                type="checkbox"
                checked={ optionSubscribedOnly }
                onChange={ (e) => setOptionSubscribedOnly(e.target.checked) }
                style={ { marginRight: "8px" } }
              />
              Assigned Cranes Only
            </label>
          </div>
          <div className="option">
            <label>From:</label>
            <DateTimeInput value={ DateTime.fromISO(optionStartDate) } timeZone={ "UTC" } onChange={ (date) => setStartDate(date) } dateOnly={ true } />
          </div>
          <div className="option">
            <label>To:</label>
            <DateTimeInput value={ DateTime.fromISO(optionEndDate) } timeZone={ "UTC" } onChange={ (date) => setEndDate(date) } dateOnly={ true } />
          </div>
        </div>

        <div className="options-section">
          <h3>Sorting</h3>

          <div className="option">
            <label>Sort By:</label>
            <div>
              <select
                value={ optionSortBy }
                onChange={ (event) => setOptionSortBy(event.target.value) }
              >
                { sortByOptions.map((option) => (
                  <option key={ option.text } value={ option.sortBy }>
                    { option.text }
                  </option>
                )) }
              </select>
            </div>
          </div>

          <div className="option">
            <label>Sort Order:</label>
            <div>
              <select
                value={ optionSortDirection }
                onChange={ (event) => setOptionSortDirection(event.target.value) }
              >
                { sortDirectionOptions.map((option) => (
                  <option key={ option.text } value={ option.sortDirection }>
                    { option.text }
                  </option>
                )) }
              </select>
            </div>
          </div>
        </div>

        <div className="options-tools">
          <Button
            onClick={ () => {
              applyOptions();
              setIsFilterMenuOpen(false);
            } }
            loading={ loading || ticketCountLoading || subscriptionLoading }
          >
            Apply
          </Button>
          <button onClick={ resetOptions }>Reset</button>
          <button onClick={ () => setIsFilterMenuOpen(false) }>Close</button>
        </div>
      </OptionsMenu.Menu>
      <OptionsMenu.Layout>
        <div className="page-header">
          <span className="page-header-title">Cranes</span>
          <div className="page-header-options">
            <Button
              onClick={ () => {
                variables.crane_list_request.pagination = { limit: 10000 };
                csvQuery({ variables }).then((response) => {
                  const csvData = convertToCSV(
                    response?.data?.crane?.edges.map((entity) => ({
                      ID: entity.id,
                      Name: entity.name,
                      Region: entity.region?.name,
                      "Last Activity": formatLastActivity(entity.last_activity_at),
                    }))
                  );
                  const blob = new Blob([csvData], {
                    type: "text/csv;charset=utf-8;",
                  });
                  const url = URL.createObjectURL(blob);

                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute(
                    "download",
                    `cranes_${DateTime.now()
                      .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
                      .replace(/:/g, "-")
                      .replace(/, /g, "_")}.csv`
                  );
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                });
              } }
              loading={ csvLoading }
            >
              <FaFileCsv />
            </Button>
            { props.isMaster && (
              <button onClick={ () => props.onSelect(0) }>
                <FaPlus />
              </button>
            ) }
            <button onClick={ () => setIsFilterMenuOpen(true) }>
              <FaCog />
            </button>
          </div>
        </div>
        <Tabs autoSelect state={ [activeTab, setActiveTab] }>
          <div label='Data' style={ { overflow: "hidden", display: "flex", flexDirection: "column", flexGrow: 1 } }>
            <input
              value={ search ?? "" }
              style={ { width: "100%" } }
              placeholder="Search ID, name, or serial number..."
              type="text"
              onChange={ (e) => {
                setListOptions({ search: e.target.value });
              } }
            />
            <ResponsiveTableView
              style={ { height: "100%" } }
              loading={ loading || ticketCountLoading || subscriptionLoading }
              headers={ [
                {
                  label: "Name",
                },
                {
                  label: "Region",
                },
                {
                  label: "Tickets"
                },
                {
                  label: "Last Activity",
                },
              ] }
              data={ list.map((entity) => ({
                id: entity.id,
                Name: entity.name || "",
                Region: entity.region?.name || "—",
                Tickets: ticketCounts[entity.id] || 0,
                "Last Activity": formatLastActivity(entity.last_activity_at),
                selected: String(entity.id) === String(entityId) && !isAdd,
              })) }
              identityColumnKey="Name"
              onRowClick={ (row) => {
                props.onSelect(row.id, true);
              } }
              totalCount={ count }
              addMoreResults={ () => {
                setLimit(limit + 100);
              } }
            />
          </div>
          <div label='Map'>
            <ListView type="crane" style={ { height: "calc(100vh - 86px)" } } onSelect={ selectItem } />
          </div>
        </Tabs>
      </OptionsMenu.Layout>
    </OptionsMenu>
  );
}
